import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import CommonPagination from "../Components/Pagination";
import {
  Box,
  Popover,
  Container,
  Tooltip,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Autocomplete,
  MenuItem,
  Switch,
  CssBaseline,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function Connector_details() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  /* ---------------- FORM ---------------- */
  const [formData, setFormData] = useState({
    connector_id: "",
    cl_id: "",
    client_name: "",
    env_id: "",
    app_id: "",
    connector_name: "",
    connection: "",
    version: "",
    active_flag: 1,
  });
  const resetForm = () => {
    setFormData({
      connector_id: "",
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      app_id: "",
      connector_name: "",
      connection: "",
      version: "",
      active_flag: 1,
    });
    setFilteredApplications([]);
    setFormErrors({});
  };
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});

  // Check if form has changed
  const hasFormChanged = () => {
    if (!isEditing) return true;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  /* ---------------- TABLE ---------------- */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConnectorId, setDeleteConnectorId] = useState(null);
  const [duplicateError, setDuplicateError] = useState("");

  /* ---------------- FILTERS ---------------- */
  const [filters, setFilters] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    connector_name: "",
  });
  const [filterAnchorEls, setFilterAnchorEls] = useState({});

  /* ---------------- COLUMN VISIBILITY ---------------- */
  const [visibleColumns, setVisibleColumns] = useState({
    cl_id: true,
    env_id: true,
    app_id: true,
    connector_name: true,
    connection: true,
    version: true,
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const columnLabels = {
    cl_id: "Client",
    env_id: "Environment",
    app_id: "Application",
    connector_name: "Connector",
    connection: "Connection",
    version: "Version",
  };

  //  Unique values for column filter dropdown
  const getUniqueValues = (key) => {
    return [...new Set(data.map((row) => row[key]).filter(Boolean))];
  };
  /* ---------------- FETCH TABLE DATA ---------------- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    // Fetch all data (non-paginated) for client-side filtering
    try {
      const result = await apiService.get(`/connectors/list`);
      setData(result || []);
    } catch (err) {
      console.error("Error fetching connectors:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------------- DROPDOWN FETCH ---------------- */
  const [loadingDropdowns, setLoadingDropdowns] = useState({
    clients: false,
    envs: false,
    apps: false,
  });
  const [dropdownErrors, setDropdownErrors] = useState({
    clients: null,
    envs: null,
    apps: null,
  });
  const [tempFilters, setTempFilters] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    connector_name: "",
    connection: "",
    version: "",
  });
  const fetchClients = async () => {
    try {
      const data = await apiService.get("/connectors/clients");
      setClients(data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/connectors/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error fetching environments:", err);
    }
  };

  const fetchApplications = async () => {
    try {
      const data = await apiService.get("/connectors/applications");
      setApplications(data);
    } catch (err) {
      console.error("Error fetching applications:", err);
    }
  };

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients]);

  useEffect(() => {
    fetchClients();
    fetchEnvironments();
    fetchApplications();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters]);

  /* ---------------- FORM HANDLERS ---------------- */

  // Fetch applications filtered by environment
  const fetchApplicationsByEnvironment = async (env_id) => {
    if (!env_id) {
      setFilteredApplications([]);
      return;
    }

    try {
      const response = await apiService.get(
        `/connectors/applications?env_id=${env_id}`,
      );
      setFilteredApplications(response);
    } catch (error) {
      console.error("Error fetching applications by environment:", error);
      setFilteredApplications([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle environment change with cascading validation
  const handleEnvironmentChange = async (event, newValue) => {
    const env_id = newValue ? newValue.env_id : "";

    setFormData((prev) => ({
      ...prev,
      env_id: env_id,
      app_id: "", // Clear application when environment changes
    }));

    setFormErrors((prev) => ({ ...prev, env_id: "" }));

    // Fetch applications for the selected environment
    if (env_id) {
      await fetchApplicationsByEnvironment(env_id);
    } else {
      setFilteredApplications([]);
    }
  };

  // Handle application change
  const handleApplicationChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      app_id: newValue ? newValue.app_id : "",
    }));
    setFormErrors((prev) => ({ ...prev, app_id: "" }));
  };

  const validate = () => {
    const err = {};
    // Client is pre-selected, no need to validate
    if (!formData.env_id) err.env_id = "Environment is required";
    if (!formData.app_id) err.app_id = "Application is required";
    if (!formData.connector_name) err.connector_name = "Connector is required";
    return err;
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setIsEditing(false);
    setFormErrors({});
    setFilteredApplications([]);
  };
  const handleSubmit = async () => {
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    const duplicate = data.some((row) => {
      const sameClient = row.cl_id === formData.cl_id;
      const sameEnv = row.env_id === formData.env_id;
      const sameApp = row.app_id === formData.app_id;

      const sameConnector =
        row.connector_name?.trim().toLowerCase() ===
        formData.connector_name?.trim().toLowerCase();

      const sameVersion =
        row.version?.trim().toLowerCase() ===
        formData.version?.trim().toLowerCase();

      const sameConnection =
        row.connection_type?.trim().toLowerCase() ===
        formData.connection_type?.trim().toLowerCase();

      const notSameRow = isEditing
        ? row.connector_id !== formData.connector_id
        : true;

      return (
        sameClient &&
        sameEnv &&
        sameApp &&
        sameConnector &&
        sameVersion &&
        sameConnection &&
        notSameRow
      );
    });
    if (duplicate) {
      setDuplicateError(
        "Connector with same Version and Connection Type already exists",
      );
      return;
    } else {
      setDuplicateError("");
    }
    try {
      if (isEditing) {
        await apiService.put(`/connectors/${formData.connector_id}`, formData);
        setSuccessMessage("Connector updated");
      } else {
        await apiService.post(`/connectors`, formData);
        setSuccessMessage("Connector added");
      }

      setOpenDialog(false);
      setIsEditing(false);
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error(err);
      alert(
        "Failed to save connector: " + (err.message || "Check server or DB."),
      );
    }
  };

  const handleEdit = async (row) => {
    setFormData(row);
    setOriginalFormData({ ...row }); // Store original for change detection

    // Fetch applications for the selected environment when editing
    if (row.env_id) {
      await fetchApplicationsByEnvironment(row.env_id);
    }

    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    setDeleteConnectorId(id);
    setOpenDeleteDialog(true);
  };

  const handleToggleFlag = async (row) => {
    const newFlag = row.active_flag === 1 ? 0 : 1;

    try {
      await apiService.request(`/connectors/${row.connector_id}/flag`, {
        method: "PATCH",
        body: JSON.stringify({ active_flag: newFlag }),
      });

      setSuccessMessage(
        `Connector ${newFlag === 1 ? "activated" : "deactivated"} successfully`,
      );
      setErrorMessage("");
      fetchData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update connector status");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };
  /* ---------------- FILTERED ROWS ---------------- */
  const filteredRows = data
    .filter((r) =>
      Object.entries(filters).every(([k, v]) =>
        r[k]?.toString().toLowerCase().includes(v.toLowerCase()),
      ),
    )
    .filter((r) =>
      searchTerm === ""
        ? true
        : Object.values(r)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
    );

  // Client-side pagination on filtered data
  const paginatedRows = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Update total to reflect filtered data count
  const filteredTotal = filteredRows.length;

  const columns = Object.keys(visibleColumns);

  /* ---------------- UI ---------------- */
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Connectors Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
         
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={() => {
                resetForm();
                setIsEditing(false);
                setOpenDialog(true);
              }}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                filteredTotal,
              )} of ${filteredTotal}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(filteredTotal / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(filteredTotal / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {columns.map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "bold",
                }}
                flex={1}
              >
                {columnLabels[col]}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((p) => ({
                    ...p,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col]}
                            <IconButton
                              onClick={(e) =>
                                setFilterAnchorEls((prev) => ({
                                  ...prev,
                                  [col]: e.currentTarget,
                                }))
                              }
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={8} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                    <TableRow key={row.connector_id}>
                      <TableCell>
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          justifyContent="flex-start"
                        >
                          {/* FLAG */}
                          <IconButton
                            size="small"
                            onClick={() => handleToggleFlag(row)}
                            title="Toggle Active Flag"
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <FlagIcon
                              style={{
                                color:
                                  parseInt(row.active_flag) === 1
                                    ? "#2E9F50"
                                    : "#FF0C0C", // Green for active, red for inactive
                                transition: "color 0.2s", // Smooth color transition
                                width: 17, // Match example size
                                height: 17, // Match example size
                                verticalAlign: "middle", // Align icon
                              }}
                            />
                          </IconButton>

                          {/* EDIT */}
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            title="Edit"
                            sx={{
                              color: "#2DB0E4",
                              "&:hover": { color: "#1976d2" },
                            }}
                          >
                            <EditOutlined sx={{ fontSize: "16px" }} />
                          </IconButton>

                          {/* DELETE */}
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.connector_id)}
                            title="Delete"
                            sx={{
                              color: "#666",
                              "&:hover": { color: "#d32f2f" },
                            }}
                          >
                            <DeleteIcon
                              style={{
                                width: 12,
                                height: 15,
                                cursor: "pointer",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                      {visibleColumns.cl_id && (
                        <TableCell>{row.client_name}</TableCell>
                      )}
                      {visibleColumns.env_id && (
                        <TableCell>{row.environment_name}</TableCell>
                      )}
                      {visibleColumns.app_id && (
                        <TableCell>{row.application_name}</TableCell>
                      )}
                      {visibleColumns.connector_name && (
                        <TableCell>{row.connector_name}</TableCell>
                      )}
                      {visibleColumns.connection && (
                        <TableCell>{row.connection}</TableCell>
                      )}
                      {visibleColumns.version && (
                        <TableCell>{row.version}</TableCell>
                      )}
                    </TableRow>
                  ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={8} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No connectors found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* COLUMN FILTER DROPDOWNS */}
            {/* {Object.keys(filterAnchorEls).map((col) => ( */}

            {columns.map((col) => (
              <Popover
                key={col}
                open={Boolean(filterAnchorEls[col])}
                anchorEl={filterAnchorEls[col]}
                onClose={() =>
                  setFilterAnchorEls((prev) => ({
                    ...prev,
                    [col]: null,
                  }))
                }
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <DialogContent>
                  <TextField
                    label={`Filter ${columnLabels[col]}`}
                    value={tempFilters[col] || ""}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        [col]: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                  <DialogActions>
                    <Button
                      onClick={() => {
                        setTempFilters((prev) => ({
                          ...prev,
                          [col]: "",
                        }));
                        setFilters((prev) => ({
                          ...prev,
                          [col]: "",
                        }));
                        setPage(1);
                        setFilterAnchorEls((prev) => ({
                          ...prev,
                          [col]: null,
                        }));
                      }}
                    >
                      Clear
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          [col]: tempFilters[col],
                        }));
                        setPage(1);
                        setFilterAnchorEls((prev) => ({
                          ...prev,
                          [col]: null,
                        }));
                      }}
                    >
                      Filter
                    </Button>
                  </DialogActions>
                </DialogContent>
              </Popover>
            ))}
          </Paper>
        </ThemeProvider>
      </Box>

      {/* ADD / EDIT DIALOG */}
      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            handleDialogClose();
          }}
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              py: 1,
            }}
          >
            {isEditing ? "Edit Connector" : "Add Connector"}
            <IconButton
              // onClick={() => setOpenDialog(false)}
              onClick={handleDialogClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box display="grid" gridTemplateColumns="repeat(3,1fr)" gap={1}>
              <TextField
                label="Client"
                value={
                  formData.client_name ||
                  clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                  ""
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                   minWidth: 320,
                  backgroundColor: "white",

                  "& .MuiInputBase-input": {
                    cursor: "not-allowed",
                    color: "grey",
                  },
                }}
              />

              <Autocomplete
                id="env_id"
                options={environments}
                getOptionLabel={(option) => option.name || ""}
                value={
                  environments.find((env) => env.env_id === formData.env_id) ||
                  null
                }
                onChange={handleEnvironmentChange}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.875rem",
                      minHeight: "32px",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Environment"
                    error={!!formErrors.env_id}
                    helperText={formErrors.env_id}
                    required
                  />
                )}
                disabled={environments.length === 0}
              />

              <Autocomplete
                id="app_id"
                options={filteredApplications}
                getOptionLabel={(option) => option.name || ""}
                value={
                  filteredApplications.find(
                    (app) => app.app_id === formData.app_id,
                  ) || null
                }
                onChange={handleApplicationChange}
                disabled={!formData.env_id || filteredApplications.length === 0}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.875rem",
                      minHeight: "32px",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Application"
                    error={!!formErrors.app_id}
                    required
                    helperText={formErrors.app_id}
                  />
                )}
              />

              <TextField
                label="Connector Name"
                name="connector_name"
                value={formData.connector_name}
                onChange={(e) => {
                  handleChange(e);
                  setDuplicateError("");
                }}
                required
                error={!!formErrors.connector_name || !!duplicateError}
                helperText={duplicateError || formErrors.connector_name || ""}
              />
              <TextField
                label="Connection"
                name="connection"
                value={formData.connection}
                onChange={handleChange}
              />
              <TextField
                label="Version"
                name="version"
                value={formData.version}
                onChange={handleChange}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            {/* <Button onClick={() => setOpenDialog(false)}>Cancel</Button> */}
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={isEditing && !hasFormChanged()}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDeleteDialog(false);
            setDeleteConnectorId(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete Connector{" "}
              <strong>
                {data.find((c) => c.connector_id === deleteConnectorId)
                  ?.connector_name || deleteConnectorId}
              </strong>
              ?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteConnectorId(null);
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(`/connectors/${deleteConnectorId}`);
                  setSuccessMessage("Connector deleted successfully!");
                  setErrorMessage("");
                  fetchData();
                  setTimeout(() => setSuccessMessage(""), 3000);
                } catch (err) {
                  console.error(err);
                  setErrorMessage("Failed to delete connector");
                  setTimeout(() => setErrorMessage(""), 3000);
                } finally {
                  setOpenDeleteDialog(false);
                  setDeleteConnectorId(null);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Container>
  );
}

export default Connector_details;
